import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  CircularProgress,
  Grid,
  Switch,
  Tabs,
  Tab,
  Tooltip,
} from '@mui/material';
import {
  Add,
  Edit,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Build as BuildIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  DeleteForever as DeleteForeverIcon,
  LockReset as LockResetIcon,
} from '@mui/icons-material';
import { FormControl, Select } from '@mui/material';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import { isValidEmail, EMAIL_HELPER } from '../../utils/kenyaValidators';
import useSetupConfig from '../../hooks/useSetupConfig';
import DataTablePagination from '../../components/Table/DataTablePagination';
import SearchField from '../../components/Table/SearchField';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { useListPage } from '../../hooks/useListPage';
import StatusLabel from '../../components/Status/StatusLabel';

const UserManagement = () => {
  const navigate = useNavigate();
  const { user: adminUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [maintenance, setMaintenance] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('The system is currently under maintenance. Please check back later.');
  const [maintenanceEndTime, setMaintenanceEndTime] = useState('');
  const [maintenanceLoading, setMaintenanceLoading] = useState(false);
  const [procurementCanDelete, setProcurementCanDelete] = useState(false);
  const [permissionSaving, setPermissionSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [tempPasswordDialog, setTempPasswordDialog] = useState({ open: false, password: '', userName: '' });
  // Permanent deletion is irreversible, so it gets a real dialog rather than a
  // window.confirm: the admin has to retype the address and give a reason,
  // which the server records in the audit log.
  const [permanentDelete, setPermanentDelete] = useState({
    open: false, user: null, confirmEmail: '', reason: '', loading: false, error: '',
  });
  const [editMode, setEditMode] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search);
  const [statusFilter, setStatusFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [sortOrder, setSortOrder] = useState('desc');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  // A new filter is a new result set; page 4 of the old one would show nothing.
  const [page, setPage] = useListPage(JSON.stringify([activeTab, debouncedSearch, rowsPerPage, statusFilter, departmentFilter]));
  const [total, setTotal] = useState(0);
  const [tabCounts, setTabCounts] = useState({ internal: 0, supplier: 0 });
  const { names: departments } = useSetupConfig('departments');
  const { names: roleNames }   = useSetupConfig('roles');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: '',
    department: '',
    employeeNumber: '',
    isActive: true,
  });

  useEffect(() => {
    fetchMaintenance();
  }, []);

  useEffect(() => {
    if (adminUser?.role !== 'super_admin') return;
    api.get('/settings/permissions')
      .then(({ data }) => setProcurementCanDelete(!!data.data?.procurementCanDeleteSuppliers))
      .catch(() => {});
  }, [adminUser?.role]);

  const handleToggleProcurementDelete = async (event) => {
    const next = event.target.checked;
    setPermissionSaving(true);
    try {
      const { data } = await api.put('/settings/permissions', { procurementCanDeleteSuppliers: next });
      setProcurementCanDelete(!!data.data?.procurementCanDeleteSuppliers);
      toast.success(next ? 'Procurement can now delete suppliers' : 'Procurement can no longer delete suppliers');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update the permission');
    } finally {
      setPermissionSaving(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, page, rowsPerPage, debouncedSearch, statusFilter, departmentFilter, sortOrder]);

  const fetchMaintenance = async () => {
    try {
      const res = await api.get('/settings/maintenance');
      setMaintenance(res.data.data?.maintenanceMode || false);
      setMaintenanceMessage(res.data.data?.maintenanceMessage || '');
      const et = res.data.data?.maintenanceEndTime;
      setMaintenanceEndTime(et ? new Date(et).toISOString().slice(0, 16) : '');
    } catch {}
  };

  const handleToggleMaintenance = async () => {
    setMaintenanceLoading(true);
    try {
      const newState = !maintenance;
      await api.put('/settings/maintenance', {
        maintenanceMode: newState,
        maintenanceMessage,
        maintenanceEndTime: maintenanceEndTime || null,
      });
      setMaintenance(newState);
      toast.success(`Maintenance mode ${newState ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update maintenance mode');
    } finally {
      setMaintenanceLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      // Filtering, sorting and paging all happen on the server. They used to be
      // done here over a limit:1000 fetch, which rendered every matching row in
      // one table and quietly lost anyone past the thousandth account.
      const params = {
        page,
        limit: rowsPerPage,
        roleGroup: activeTab === 0 ? 'internal' : 'supplier',
        sortOrder,
      };
      if (debouncedSearch) params.search = debouncedSearch;
      if (statusFilter) params.status = statusFilter;
      if (departmentFilter) params.department = departmentFilter;

      const response = await api.get('/users', { params });
      const normalizedUsers = (response.data.data || []).map((user) => ({
        ...user,
        id: user.id || user._id,
      }));
      setUsers(normalizedUsers);
      setTotal(response.data.pagination?.total || 0);
      setTabCounts(response.data.counts || { internal: 0, supplier: 0 });
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (user = null) => {
    if (user) {
      setEditMode(true);
      setCurrentUser(user);
      setFormData({
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        role: user.role,
        department: user.department || '',
        employeeNumber: user.employeeNumber || '',
        isActive: user.isActive !== false,
      });
    } else {
      setEditMode(false);
      setCurrentUser(null);
      setFormData({ firstName: '', lastName: '', email: '', role: '', department: '', employeeNumber: '', isActive: true });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditMode(false);
    setCurrentUser(null);
    setFormData({ firstName: '', lastName: '', email: '', role: '', department: '', employeeNumber: '', isActive: true });
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
      ...(field === 'role' && value !== 'management' ? { department: '' } : {}),
    }));
  };

  const handleSubmit = async () => {
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.role) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (formData.email && !isValidEmail(formData.email)) {
      toast.error('Email address format is invalid. ' + EMAIL_HELPER);
      return;
    }
    if (formData.role === 'management' && !formData.department?.trim()) {
      toast.error('Department is required for management users');
      return;
    }
    try {
      if (editMode) {
        const userId = currentUser?.id || currentUser?._id;
        if (!userId) { toast.error('Unable to update user: missing user id'); return; }
        await api.put(`/users/${userId}`, {
          firstName: formData.firstName,
          lastName: formData.lastName,
          role: formData.role,
          department: formData.department,
          employeeNumber: formData.employeeNumber,
          isActive: formData.isActive,
        });
        toast.success('User updated successfully');
      } else {
        const res = await api.post('/users', {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          role: formData.role,
          department: formData.department,
          employeeNumber: formData.employeeNumber,
        });
        handleCloseDialog();
        fetchUsers();
        setTempPasswordDialog({
          open: true,
          password: res.data.tempPassword,
          userName: `${formData.firstName} ${formData.lastName}`.trim(),
        });
        return;
      }
      handleCloseDialog();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.message || `Failed to ${editMode ? 'update' : 'create'} user`);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await api.delete(`/users/${userId}`);
      toast.success('User deleted successfully');
      handleCloseDialog();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete user');
    }
  };

  const openPermanentDelete = (user) => {
    setPermanentDelete({ open: true, user, confirmEmail: '', reason: '', loading: false, error: '' });
  };

  const closePermanentDelete = () => {
    setPermanentDelete({ open: false, user: null, confirmEmail: '', reason: '', loading: false, error: '' });
  };

  const handlePermanentDelete = async () => {
    const { user, confirmEmail, reason } = permanentDelete;
    const userId = user?.id || user?._id;
    if (!userId) return;

    if (confirmEmail.trim().toLowerCase() !== (user.email || '').toLowerCase()) {
      setPermanentDelete((s) => ({ ...s, error: 'The email address does not match.' }));
      return;
    }
    if (!reason.trim()) {
      setPermanentDelete((s) => ({ ...s, error: 'A reason is required.' }));
      return;
    }

    setPermanentDelete((s) => ({ ...s, loading: true, error: '' }));
    try {
      const res = await api.delete(`/users/${userId}/permanent`, { data: { reason: reason.trim() } });
      toast.success(res.data?.message || 'User permanently deleted');
      closePermanentDelete();
      handleCloseDialog();
      fetchUsers();
    } catch (error) {
      // A live contract or the last-super-admin guard comes back here; the
      // server's message says which, so surface it in the dialog rather than a
      // toast the admin has to chase.
      setPermanentDelete((s) => ({
        ...s,
        loading: false,
        error: error.response?.data?.message || 'Failed to permanently delete user',
      }));
    }
  };

  const handleResetPassword = async (user) => {
    const userId = user.id || user._id;
    if (!window.confirm(`Reset password for ${user.firstName} ${user.lastName}? They will be prompted to set a new password on next login.`)) return;
    try {
      const res = await api.post(`/users/${userId}/reset-password`);
      setTempPasswordDialog({
        open: true,
        password: res.data.tempPassword,
        userName: `${user.firstName} ${user.lastName}`
      });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to reset password');
    }
  };

  const handleToggleActive = async (user) => {
    const userId = user.id || user._id;
    const action = user.isActive ? 'suspend' : 'activate';
    if (!window.confirm(`Are you sure you want to ${action} this user?`)) return;
    try {
      await api.put(`/users/${userId}`, {
        firstName: user.firstName,
        lastName: user.lastName,
        role: user.role,
        department: user.department || '',
        phone: user.phone || '',
        isActive: !user.isActive,
      });
      toast.success(`User ${action}d successfully`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.message || `Failed to ${action} user`);
    }
  };

  const getStatusChip = (user) => {
    if (user.role === 'supplier' && user.isEmailVerified === false) {
      return <StatusLabel kind="user" status="pending_verification" label="Pending Verification" />;
    }
    return <StatusLabel kind="user" status={user.isActive ? 'active' : 'suspended'} label={user.isActive ? 'Active' : 'Suspended'} />;
  };

  const getDisplayPhone = (user) => {
    return user.phone || user.supplier?.authorizedPerson?.phone || user.supplierContactPhone || '-';
  };

  const getSupplierName = (user) => {
    return user.supplier?.supplierName || '-';
  };

  const FALLBACK_ROLES = [
    { value: 'super_admin', label: 'Super Admin' },
    { value: 'procurement', label: 'Procurement' },
    { value: 'legal',       label: 'Legal' },
    { value: 'management',  label: 'Management' },
  ];
  const roleOptions = roleNames.length > 0
    ? roleNames.map(name => ({
        value: name.toLowerCase().replace(/\s+/g, '_'),
        label: name,
      }))
    : FALLBACK_ROLES;


  // `users` is already the filtered, sorted, paged group the active tab asked
  // for — the server does all of it now.

  const filterBar = (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
      <SearchField
        value={search}
        onChange={setSearch}
        placeholder="Search name, email, phone, company"
        sx={{ minWidth: { xs: '100%', sm: '180px' }, maxWidth: { xs: '100%', sm: '300px' } }}
      />
      <FormControl size="small" sx={{ minWidth: 160 }}>
        <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} displayEmpty sx={{ borderRadius: '8px', fontSize: '14px', backgroundColor: '#fff' }}>
          <MenuItem value="">All Statuses</MenuItem>
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="inactive">Suspended</MenuItem>
          {activeTab === 1 && <MenuItem value="pending_verification">Pending Verification</MenuItem>}
        </Select>
      </FormControl>
      {activeTab === 0 && (
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <Select value={departmentFilter} onChange={(e) => setDepartmentFilter(e.target.value)} displayEmpty sx={{ borderRadius: '8px', fontSize: '14px', backgroundColor: '#fff' }}>
            <MenuItem value="">All Departments</MenuItem>
            {departments.map((dept) => (
              <MenuItem key={dept} value={dept}>{dept}</MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      <Tooltip title={sortOrder === 'asc' ? 'Oldest first — click for newest first' : 'Newest first — click for oldest first'}>
        <Button variant="outlined" size="small" onClick={() => setSortOrder((s) => (s === 'asc' ? 'desc' : 'asc'))} startIcon={sortOrder === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />} sx={{ borderColor: '#d1d5db', color: '#374151', textTransform: 'none', fontSize: '14px', px: 2, py: 1, borderRadius: '8px', whiteSpace: 'nowrap', '&:hover': { borderColor: '#9ca3af', bgcolor: '#f9fafb' } }}>
          {sortOrder === 'asc' ? 'Oldest first' : 'Newest first'}
        </Button>
      </Tooltip>
    </Box>
  );

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#fff', display: 'flex', flexDirection: 'column', pb: 0 }}>
      <Container maxWidth="lg" sx={{ pt: { xs: 3, sm: 5 }, pb: { xs: 3, sm: 4 }, px: { xs: 2, sm: 3 }, flex: 1 }}>
        <Box sx={{ mb: { xs: 2.5, sm: 3 } }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5, color: '#111827', fontSize: { xs: '28px', sm: '28px' }, letterSpacing: '-0.01em', lineHeight: 1.1 }}>
            Users
          </Typography>
          <Typography sx={{ color: '#6b7280', fontSize: { xs: '15px', sm: '14px' }, lineHeight: 1.5 }}>
            Manage and configure users of the platform
          </Typography>
        </Box>

        {/* Maintenance Mode Toggle */}
        {adminUser?.role === 'super_admin' && (
          <Paper
            elevation={0}
            sx={{
              border: `1px solid ${maintenance ? '#fca5a5' : '#e5e7eb'}`,
              borderRadius: '10px',
              p: 2,
              mb: 3,
              backgroundColor: maintenance ? '#fff7f7' : '#fff',
              display: 'flex',
              alignItems: { xs: 'flex-start', sm: 'center' },
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
              <Box sx={{ width: 40, height: 40, borderRadius: '8px', backgroundColor: maintenance ? '#fee2e2' : '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <BuildIcon sx={{ fontSize: 20, color: maintenance ? '#dc2626' : '#6b7280' }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#111827' }}>
                  Maintenance Mode
                  {maintenance && (
                    <Chip label="ON" size="small" sx={{ ml: 1, backgroundColor: '#dc2626', color: '#fff', fontSize: '11px', height: '20px', fontWeight: 700 }} />
                  )}
                </Typography>
                <Typography sx={{ fontSize: '13px', color: '#6b7280' }}>
                  {maintenance ? 'All non-admin users see a maintenance page' : 'Platform is fully operational'}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: { xs: '100%', sm: 'auto' }, flexWrap: 'wrap', flex: 1 }}>
              <TextField
                size="small"
                placeholder="Maintenance message (optional)"
                value={maintenanceMessage}
                onChange={(e) => setMaintenanceMessage(e.target.value)}
                sx={{ flex: 1, minWidth: { xs: 0, sm: 380 }, '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '13px' } }}
              />
              <Button
                variant={maintenance ? 'outlined' : 'contained'}
                onClick={handleToggleMaintenance}
                disabled={maintenanceLoading}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '13px',
                  px: 2,
                  py: 1,
                  borderRadius: '8px',
                  boxShadow: 'none',
                  whiteSpace: 'nowrap',
                  ...(maintenance
                    ? { borderColor: '#16a34a', color: '#16a34a', '&:hover': { borderColor: '#15803d', backgroundColor: '#f0fdf4' } }
                    : { backgroundColor: '#dc2626', '&:hover': { backgroundColor: '#b91c1c', boxShadow: 'none' } }
                  ),
                }}
              >
                {maintenanceLoading ? 'Saving...' : maintenance ? 'Disable Maintenance' : 'Enable Maintenance'}
              </Button>
            </Box>
          </Paper>
        )}

        {/* Permission switches */}
        {adminUser?.role === 'super_admin' && (
          <Paper
            elevation={0}
            sx={{
              border: '1px solid #e5e7eb',
              borderRadius: '10px',
              p: 2,
              mb: 3,
              backgroundColor: '#fff',
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Box sx={{ width: 40, height: 40, borderRadius: '8px', backgroundColor: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <DeleteIcon sx={{ fontSize: 20, color: '#6b7280' }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#111827' }}>
                Procurement can delete suppliers
              </Typography>
              <Typography sx={{ fontSize: '13px', color: '#6b7280' }}>
                {procurementCanDelete
                  ? 'Procurement can permanently delete a supplier in draft or procurement review, with a reason'
                  : 'Only super admins can delete suppliers'}
              </Typography>
            </Box>
            <Switch
              checked={procurementCanDelete}
              onChange={handleToggleProcurementDelete}
              disabled={permissionSaving}
              inputProps={{ 'aria-label': 'Procurement can delete suppliers' }}
              sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#fff' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#65a30d' } }}
            />
          </Paper>
        )}

        {/* Tabs */}
        <Box sx={{ borderBottom: '1px solid #e5e7eb', mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => { setActiveTab(v); setStatusFilter(''); setDepartmentFilter(''); setSearch(''); }}
            sx={{
              '& .MuiTab-root': { textTransform: 'none', fontSize: '14px', fontWeight: 500, color: '#6b7280', minWidth: 0, px: 0, mr: 3 },
              '& .Mui-selected': { color: '#111827', fontWeight: 600 },
              '& .MuiTabs-indicator': { backgroundColor: '#578A18' },
            }}
          >
            <Tab label={`Internal Users (${tabCounts.internal})`} />
            <Tab label={`Suppliers (${tabCounts.supplier})`} />
          </Tabs>
        </Box>

        {/* Add User button — only for internal tab */}
        {activeTab === 0 && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
            sx={{ bgcolor: '#578A18', color: '#fff', textTransform: 'none', fontSize: '14px', fontWeight: 600, px: 2.5, py: 1, borderRadius: '8px', width: { xs: '100%', sm: 'auto' }, alignSelf: 'flex-start', boxShadow: 'none', mb: 2, '&:hover': { bgcolor: '#467014', boxShadow: 'none' } }}
          >
            Add New User
          </Button>
        )}

        {filterBar}

        {/* Internal Users Table */}
        {activeTab === 0 && (
          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden', backgroundColor: '#fff' }}>
            <Table sx={{ minWidth: { xs: 540, md: 920 } }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                  <TableCell sx={{ fontSize: '13px', fontWeight: 500, color: '#4b5563', py: 1.5 }}>Full Name</TableCell>
                  <TableCell sx={{ fontSize: '13px', fontWeight: 500, color: '#4b5563', py: 1.5, display: { xs: 'none', md: 'table-cell' } }}>Email Address</TableCell>
                  <TableCell sx={{ fontSize: '13px', fontWeight: 500, color: '#4b5563', py: 1.5, display: { xs: 'none', md: 'table-cell' } }}>SN Number</TableCell>
                  <TableCell sx={{ fontSize: '13px', fontWeight: 500, color: '#4b5563', py: 1.5, display: { xs: 'none', md: 'table-cell' } }}>Role</TableCell>
                  <TableCell sx={{ fontSize: '13px', fontWeight: 500, color: '#4b5563', py: 1.5 }}>Status</TableCell>
                  <TableCell align="right" sx={{ fontSize: '13px', fontWeight: 500, color: '#4b5563', py: 1.5 }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.length === 0 ? (
                  <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}><Typography sx={{ color: '#6b7280', fontSize: '13px' }}>No internal users found</Typography></TableCell></TableRow>
                ) : (
                  users.map((user) => (
                    <TableRow key={user.id || user._id} hover sx={{ '&:hover': { backgroundColor: '#f9fafb' } }}>
                      <TableCell sx={{ fontSize: '14px', color: '#111827', py: 1.5 }}>
                        {user.firstName} {user.lastName}
                      </TableCell>
                      <TableCell sx={{ fontSize: '14px', color: '#111827', py: 1.5, display: { xs: 'none', md: 'table-cell' } }}>{user.email}</TableCell>
                      <TableCell sx={{ fontSize: '14px', color: '#111827', py: 1.5, display: { xs: 'none', md: 'table-cell' } }}>{user.employeeNumber || '-'}</TableCell>
                      <TableCell sx={{ fontSize: '14px', color: '#111827', py: 1.5, display: { xs: 'none', md: 'table-cell' } }}>
                        {user.role?.replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>{getStatusChip(user)}</TableCell>
                      <TableCell align="right" sx={{ py: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                          <Tooltip title={user.isActive ? 'Suspend' : 'Activate'}>
                            <IconButton size="small" onClick={() => handleToggleActive(user)} sx={{ color: user.isActive ? '#dc2626' : '#65a30d' }}>
                              {user.isActive ? <BlockIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reset Password">
                            <IconButton size="small" onClick={() => handleResetPassword(user)} sx={{ color: '#f59e0b' }}>
                              <LockResetIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => handleOpenDialog(user)} sx={{ color: '#111827' }}>
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete permanently">
                            <IconButton size="small" onClick={() => openPermanentDelete(user)} sx={{ color: '#6b7280', '&:hover': { color: '#b91c1c' } }}>
                              <DeleteForeverIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <DataTablePagination
              page={page}
              total={total}
              rowsPerPage={rowsPerPage}
              onPageChange={setPage}
              onRowsPerPageChange={setRowsPerPage}
              loading={loading}
            />
          </TableContainer>
        )}

        {/* Suppliers Table */}
        {activeTab === 1 && (
          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden', backgroundColor: '#fff' }}>
            <Table sx={{ minWidth: { xs: 540, md: 960 } }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                  <TableCell sx={{ fontSize: '13px', fontWeight: 500, color: '#4b5563', py: 1.5 }}>Contact Name</TableCell>
                  <TableCell sx={{ fontSize: '13px', fontWeight: 500, color: '#4b5563', py: 1.5, display: { xs: 'none', sm: 'table-cell' } }}>Company</TableCell>
                  <TableCell sx={{ fontSize: '13px', fontWeight: 500, color: '#4b5563', py: 1.5, display: { xs: 'none', md: 'table-cell' } }}>Email Address</TableCell>
                  <TableCell sx={{ fontSize: '13px', fontWeight: 500, color: '#4b5563', py: 1.5, display: { xs: 'none', md: 'table-cell' } }}>SN Number</TableCell>
                  <TableCell sx={{ fontSize: '13px', fontWeight: 500, color: '#4b5563', py: 1.5 }}>Status</TableCell>
                  <TableCell align="right" sx={{ fontSize: '13px', fontWeight: 500, color: '#4b5563', py: 1.5 }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.length === 0 ? (
                  <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}><Typography sx={{ color: '#6b7280', fontSize: '13px' }}>No suppliers found</Typography></TableCell></TableRow>
                ) : (
                  users.map((user) => {
                    const supplierId = user.supplier?._id || user.supplier;
                    return (
                      <TableRow key={user.id || user._id} hover sx={{ '&:hover': { backgroundColor: '#f9fafb' } }}>
                        <TableCell sx={{ fontSize: '14px', color: '#111827', py: 1.5 }}>
                          {user.firstName} {user.lastName}
                        </TableCell>
                        <TableCell sx={{ fontSize: '14px', color: '#111827', py: 1.5, display: { xs: 'none', sm: 'table-cell' } }}>
                          {getSupplierName(user)}
                        </TableCell>
                        <TableCell sx={{ fontSize: '14px', color: '#111827', py: 1.5, display: { xs: 'none', md: 'table-cell' } }}>{user.email}</TableCell>
                        <TableCell sx={{ fontSize: '14px', color: '#111827', py: 1.5, display: { xs: 'none', md: 'table-cell' } }}>{getDisplayPhone(user)}</TableCell>
                        <TableCell sx={{ py: 1.5 }}>{getStatusChip(user)}</TableCell>
                        <TableCell align="right" sx={{ py: 1.5 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                            {supplierId && (
                              <Tooltip title="View Profile">
                                <IconButton size="small" onClick={() => navigate(`/suppliers/${supplierId}`)} sx={{ color: '#111827' }}>
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title={user.isActive ? 'Suspend' : 'Activate'}>
                              <IconButton size="small" onClick={() => handleToggleActive(user)} sx={{ color: user.isActive ? '#dc2626' : '#65a30d' }}>
                                {user.isActive ? <BlockIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Deactivate (reversible)">
                              <IconButton size="small" onClick={() => handleDeleteUser(user.id || user._id)} sx={{ color: '#6b7280', '&:hover': { color: '#dc2626' } }}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete permanently">
                              <IconButton size="small" onClick={() => openPermanentDelete(user)} sx={{ color: '#6b7280', '&:hover': { color: '#b91c1c' } }}>
                                <DeleteForeverIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
            <DataTablePagination
              page={page}
              total={total}
              rowsPerPage={rowsPerPage}
              onPageChange={setPage}
              onRowsPerPageChange={setRowsPerPage}
              loading={loading}
            />
          </TableContainer>
        )}
      </Container>

      {/* Add/Edit Internal User Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: { xs: '18px 18px 0 0', sm: '10px' }, p: 0, m: { xs: 0, sm: 2 }, position: { xs: 'fixed', sm: 'relative' }, bottom: { xs: 0, sm: 'auto' }, width: { xs: '100%', sm: 'auto' } } }}>
        <DialogTitle sx={{ pb: 1, pt: 2.5, px: 2.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography sx={{ fontSize: { xs: '28px', sm: '28px' }, fontWeight: 700, color: '#111827', lineHeight: 1.15 }}>
                {editMode ? 'Edit User' : 'Create User'}
              </Typography>
              <Typography sx={{ color: '#6b7280', fontSize: '14px', mt: 0.75 }}>
                {editMode ? 'Update user details' : 'Add user details to invite them to the platform'}
              </Typography>
            </Box>
            <IconButton onClick={handleCloseDialog} sx={{ color: '#111827', mt: -0.5, mr: -0.5 }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ px: 2.5, pb: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography sx={{ fontSize: '14px', color: '#111827', mb: 0.75 }}>First name</Typography>
              <TextField fullWidth value={formData.firstName} onChange={(e) => handleChange('firstName', e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', '& input': { fontSize: '14px' } } }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography sx={{ fontSize: '14px', color: '#111827', mb: 0.75 }}>Last name</Typography>
              <TextField fullWidth value={formData.lastName} onChange={(e) => handleChange('lastName', e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', '& input': { fontSize: '14px' } } }} />
            </Grid>
            <Grid item xs={12}>
              <Typography sx={{ fontSize: '14px', color: '#111827', mb: 0.75 }}>Work email address</Typography>
              <TextField fullWidth type="email" value={formData.email} onChange={(e) => handleChange('email', e.target.value)} disabled={editMode} error={!editMode && !!formData.email && !isValidEmail(formData.email)} helperText={!editMode && formData.email && !isValidEmail(formData.email) ? EMAIL_HELPER : ''} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', '& input': { fontSize: '14px' } } }} />
            </Grid>
            <Grid item xs={12}>
              <Typography sx={{ fontSize: '14px', color: '#111827', mb: 0.75 }}>SN Number</Typography>
              <TextField fullWidth value={formData.employeeNumber} onChange={(e) => handleChange('employeeNumber', e.target.value)} placeholder="e.g. EMP-001" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', '& input': { fontSize: '14px' } } }} />
            </Grid>
            <Grid item xs={12}>
              <Typography sx={{ fontSize: '14px', color: '#111827', mb: 0.75 }}>Role *</Typography>
              <TextField fullWidth select value={formData.role} onChange={(e) => handleChange('role', e.target.value)} SelectProps={{ displayEmpty: true }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}>
                <MenuItem value="" disabled>Select role</MenuItem>
                {roleOptions.map((r) => (
                  <MenuItem key={r.value} value={r.value} sx={{ fontSize: '14px' }}>{r.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            {formData.role === 'management' && (
              <Grid item xs={12}>
                <Typography sx={{ fontSize: '14px', color: '#111827', mb: 0.75 }}>Department *</Typography>
                <TextField fullWidth select value={formData.department} onChange={(e) => handleChange('department', e.target.value)} SelectProps={{ displayEmpty: true }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}>
                  <MenuItem value="" disabled>Select Department</MenuItem>
                  {departments.map((dept) => (
                    <MenuItem key={dept} value={dept} sx={{ fontSize: '14px' }}>{dept}</MenuItem>
                  ))}
                  {formData.department && !departments.includes(formData.department) && (
                    <MenuItem value={formData.department} sx={{ fontSize: '14px' }}>{formData.department}</MenuItem>
                  )}
                </TextField>
              </Grid>
            )}
            {editMode && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Switch checked={!!formData.isActive} onChange={(e) => handleChange('isActive', e.target.checked)} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#fff' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#65a30d' } }} />
                  <Typography sx={{ fontSize: '14px', color: '#111827' }}>Active User</Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2.5, pt: 1.5, flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5 }}>
          {editMode && currentUser?.role !== 'super_admin' && (
            <Button fullWidth variant="outlined" color="error" onClick={() => handleDeleteUser(currentUser?.id || currentUser?._id)} sx={{ textTransform: 'none', borderRadius: '8px' }}>
              Deactivate
            </Button>
          )}
          {editMode && (
            <Button
              fullWidth
              variant="outlined"
              onClick={() => openPermanentDelete(currentUser)}
              startIcon={<DeleteForeverIcon />}
              sx={{ textTransform: 'none', borderRadius: '8px', color: '#b91c1c', borderColor: '#fca5a5', '&:hover': { borderColor: '#b91c1c', backgroundColor: '#fef2f2' } }}
            >
              Delete permanently
            </Button>
          )}
          <Button fullWidth variant="contained" onClick={handleSubmit} sx={{ textTransform: 'none', borderRadius: '8px', boxShadow: 'none', bgcolor: '#578A18', '&:hover': { bgcolor: '#467014', boxShadow: 'none' } }}>
            {editMode ? 'Save User' : 'Invite User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Permanent delete confirmation.
          Two deliberate frictions: the exact email has to be retyped, and a
          reason is mandatory — the server refuses without one and stores it in
          the audit log, which is the only record left once the account is gone. */}
      <Dialog open={permanentDelete.open} onClose={permanentDelete.loading ? undefined : closePermanentDelete} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '10px' } }}>
        <DialogTitle sx={{ pb: 1, pt: 2.5, px: 2.5 }}>
          <Typography sx={{ fontSize: '22px', fontWeight: 700, color: '#b91c1c', lineHeight: 1.2 }}>
            Permanently delete this user?
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ px: 2.5 }}>
          <Typography sx={{ fontSize: '14px', color: '#374151', mb: 2 }}>
            This cannot be undone. It removes the account for{' '}
            <strong>{permanentDelete.user?.email}</strong>
            {permanentDelete.user?.role === 'supplier'
              ? ', their supplier profile and every uploaded document, including the files on disk.'
              : '.'}
          </Typography>

          <Box sx={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: '8px', p: 1.5, mb: 2.5 }}>
            <Typography sx={{ fontSize: '12px', color: '#7a5c00', lineHeight: 1.6 }}>
              Contracts and the audit trail are kept — they record what the business
              actually did. To take an account out of use reversibly, use Deactivate instead.
            </Typography>
          </Box>

          {permanentDelete.error && (
            <Box sx={{ mb: 2, p: 1.5, backgroundColor: '#ffebee', borderRadius: '8px', color: '#c62828', fontSize: '13px' }}>
              {permanentDelete.error}
            </Box>
          )}

          <Typography sx={{ fontSize: '14px', color: '#111827', mb: 0.75 }}>
            Type <strong>{permanentDelete.user?.email}</strong> to confirm
          </Typography>
          <TextField
            fullWidth
            autoComplete="off"
            value={permanentDelete.confirmEmail}
            onChange={(e) => setPermanentDelete((st) => ({ ...st, confirmEmail: e.target.value, error: '' }))}
            sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: '10px', '& input': { fontSize: '14px' } } }}
          />

          <Typography sx={{ fontSize: '14px', color: '#111827', mb: 0.75 }}>Reason *</Typography>
          <TextField
            fullWidth
            multiline
            minRows={2}
            placeholder="e.g. duplicate registration, requested erasure"
            value={permanentDelete.reason}
            onChange={(e) => setPermanentDelete((st) => ({ ...st, reason: e.target.value, error: '' }))}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '14px' } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2.5, pt: 1.5, gap: 1.5 }}>
          <Button fullWidth variant="outlined" disabled={permanentDelete.loading} onClick={closePermanentDelete} sx={{ textTransform: 'none', borderRadius: '8px', color: '#374151', borderColor: '#d1d5db' }}>
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            disabled={permanentDelete.loading}
            onClick={handlePermanentDelete}
            sx={{ textTransform: 'none', borderRadius: '8px', boxShadow: 'none', bgcolor: '#b91c1c', '&:hover': { bgcolor: '#991b1b', boxShadow: 'none' } }}
          >
            {permanentDelete.loading ? 'Deleting...' : 'Delete permanently'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Temp password reveal dialog */}
      <Dialog open={tempPasswordDialog.open} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>User Created</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2, color: '#555', fontSize: '14px' }}>
Share the temporary password below with <strong>{tempPasswordDialog.userName}</strong> directly — it will not be shown again. They will be prompted to set a new password on first login.
          </Typography>
          <Box sx={{ background: '#f5f5f5', border: '1px solid #e0e0e0', borderRadius: '8px', p: 2, textAlign: 'center', mb: 2 }}>
            <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '20px', letterSpacing: '2px', color: '#111' }}>
              {tempPasswordDialog.password}
            </Typography>
          </Box>
          <Box sx={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: '8px', p: 1.5 }}>
            <Typography sx={{ fontSize: '12px', color: '#7a5c00' }}>
              The user will be prompted to change this password on first login.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2.5 }}>
          <Button
            fullWidth
            variant="contained"
            onClick={() => setTempPasswordDialog({ open: false, password: '', userName: '' })}
            sx={{ textTransform: 'none', borderRadius: '8px', boxShadow: 'none', bgcolor: '#578A18', '&:hover': { bgcolor: '#467014', boxShadow: 'none' } }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;
